// Billing page (Phase 5.5). Three panels stacked:
//   • Plan summary — reads from GET /v1/usage (already cached from the Usage
//     page in most nav flows). Shows plan code, status, period, and frozen
//     badge.
//   • Portal handoff — POSTs /v1/billing/portal and redirects the browser to
//     the returned Stripe URL. Catches 402 (pre-checkout tenant) and 404
//     (Stripe not wired on this environment) with distinct copy.
//   • Invoices — GET /v1/billing/invoices. Renders status badge, amount (from
//     Stripe cents), and links to the hosted invoice page / PDF when present.
//
// Why piggyback on /v1/usage for plan state instead of adding /v1/plan:
// the caller already sees the same plan header on Usage. Two sources of
// truth for the same field invites drift. The cached query makes the
// second page visit free.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink, FileText, Receipt, RotateCcw } from 'lucide-react';
import type { InvoiceSummary, UsageSummary } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError } from '../../lib/api-client.js';
import { billingApi, INVOICES_QUERY_KEY, useResumeSubscription } from '../../lib/api/billing.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { formatDate } from '../../lib/format-date.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';

export function BillingPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing"
        description="Manage the plan, payment method, and invoices. Updates open in the Stripe Billing Portal."
      />
      <PlanCard />
      <InvoicesCard />
    </div>
  );
}

function PlanCard() {
  const api = useApiClient();
  // Override the global QueryClient defaults (refetchOnWindowFocus=false,
  // staleTime=30s) for this page only — when the user resumes via Stripe
  // Portal's "Don't cancel subscription" button in another tab and switches
  // back, the cancel banner needs to clear without a manual refresh.
  const summary = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  if (summary.isLoading) {
    return (
      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-9 w-40" />
        </CardContent>
      </Card>
    );
  }
  if (summary.isError) {
    return <ErrorRetry title="Could not load plan" error={summary.error} onRetry={() => void summary.refetch()} />;
  }
  if (!summary.data) return <></>;

  return <PlanCardBody summary={summary.data} />;
}

function PlanCardBody({ summary }: { summary: UsageSummary }) {
  const { plan } = summary;
  const isNoSub = plan.code === 'no_subscription';
  const start = formatDate(plan.currentPeriodStart);
  const end = formatDate(plan.currentPeriodEnd);
  // `cancelAt` set means the customer has scheduled cancellation (via
  // Stripe Portal). Sub stays `active` until cancelAt; until then we
  // surface a banner + Resume button and gate plan-switching.
  const cancelDate = plan.cancelAt ? formatDate(plan.cancelAt) : null;
  const isScheduledCancel = cancelDate !== null && !plan.frozen;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary" aria-hidden />
          {isNoSub ? 'No subscription' : 'Current plan'}
        </CardTitle>
        <CardDescription>
          {isNoSub
            ? 'This tenant has not completed checkout yet. Once the Stripe subscription is active the portal unlocks.'
            : `Plan: ${plan.code} · Status: ${plan.status} · Period ${start} → ${end}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {plan.frozen && (
          <div
            role="status"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            Billing is frozen
            {plan.frozenReason ? ` — ${plan.frozenReason}` : ''}. New chats are rejected with 429
            until this clears.
          </div>
        )}
        {isScheduledCancel && (
          <div
            role="status"
            className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning-foreground"
          >
            Subscription cancels on {cancelDate}. After that you continue on the Free tier with reduced limits — your data stays. Resume to keep your current plan.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <OpenPortalButton disabled={isNoSub} />
          {isScheduledCancel ? (
            // Hide "Change plan" while a cancellation is scheduled —
            // switching plans during the grace period would not clear
            // the cancellation (Stripe's items update is independent of
            // cancel_at_period_end). Resume is the only forward action;
            // backend gate (409) is the belt-and-braces.
            <ResumeSubscriptionButton />
          ) : (
            <Button asChild variant={isNoSub ? 'default' : 'secondary'}>
              <Link to="/billing/upgrade" aria-label="View plans">
                {isNoSub ? 'Choose a plan' : 'Change plan'}
                <ArrowRight aria-hidden />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ResumeSubscriptionButton() {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resume = useResumeSubscription();

  const onResume = () => {
    setError(null);
    resume.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setError('Stripe billing is not configured on this environment.');
            return;
          }
          if (err.status === 402 || err.status === 409) {
            // 402 = no Stripe customer, 409 = nothing to resume. Both indicate
            // /v1/usage was stale — the next refetch will reconcile.
            setError('Subscription state changed. Refresh the page and try again.');
            return;
          }
        }
        setError(err instanceof Error ? err.message : 'Could not resume subscription.');
      },
    });
  };

  if (!confirming) {
    return (
      <Button
        onClick={() => { setConfirming(true); }}
        aria-label="Resume subscription"
        variant="default"
      >
        <RotateCcw aria-hidden />
        Resume subscription
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        onClick={onResume}
        disabled={resume.isPending}
        aria-label="Confirm resume subscription"
        variant="default"
      >
        {resume.isPending ? 'Resuming…' : 'Confirm resume'}
      </Button>
      <Button
        onClick={() => { setConfirming(false); }}
        disabled={resume.isPending}
        aria-label="Cancel resume"
        variant="ghost"
      >
        Cancel
      </Button>
      {error && (
        <p role="alert" className="w-full text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function OpenPortalButton({ disabled }: { disabled: boolean }) {
  const api = useApiClient();
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () =>
      billingApi.createPortalSession(api, { returnUrl: window.location.href }),
    onSuccess: ({ url }) => {
      setError(null);
      window.location.assign(url);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(
            'Stripe billing is not configured on this environment. Ask an admin to set STRIPE_API_KEY.',
          );
          return;
        }
        if (err.status === 402) {
          setError(
            'No Stripe subscription yet. Complete checkout before opening the portal.',
          );
          return;
        }
        if (err.status === 409 && err.code === 'invalid_state') {
          // Most often hit on dev environments seeded with a synthetic
          // Stripe customer ID that doesn't exist in the connected account.
          // The fix is to recreate the subscription through Stripe Checkout.
          setError(
            'This subscription points to a Stripe customer that no longer exists. Use Change plan to recreate it.',
          );
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not open portal.');
    },
  });

  return (
    <div className="space-y-2">
      <Button
        onClick={() => { open.mutate(); }}
        disabled={disabled || open.isPending}
        aria-label="Open Stripe billing portal"
        variant="outline"
      >
        <ExternalLink aria-hidden />
        {open.isPending ? 'Redirecting…' : 'Open billing portal'}
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function InvoicesCard() {
  const api = useApiClient();
  const invoices = useQuery({
    queryKey: INVOICES_QUERY_KEY,
    queryFn: () => billingApi.listInvoices(api),
    // A 404 here means Stripe isn't wired on this environment. We still show
    // the card (with a clarifying message) rather than hiding it — operators
    // need to notice the config gap.
    retry: false,
  });

  if (invoices.isLoading) {
    return (
      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-2 p-6 pt-0">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }
  if (invoices.isError) {
    const status = invoices.error instanceof ApiError ? invoices.error.status : undefined;
    const notConfigured = status === 404;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            {notConfigured
              ? 'Invoice history shows up once Stripe billing is configured on this environment.'
              : 'Could not load invoices.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (!invoices.data) return <></>;

  const { invoices: list } = invoices.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
        <CardDescription>
          Most recent first. Click the number to open the hosted invoice or the icon to download
          the PDF.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {list.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            No invoices yet — Stripe emits the first one at the end of the billing period.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  paid: 'success',
  open: 'warning',
  uncollectible: 'destructive',
  void: 'muted',
  draft: 'muted',
};

function InvoiceRow({ invoice }: { invoice: InvoiceSummary }) {
  // Stripe `created` is Unix seconds — convert to ISO so formatDate's
  // string contract holds.
  const date = formatDate(new Date(invoice.created * 1000).toISOString());
  const number = invoice.number ?? invoice.id;
  return (
    <TableRow>
      <TableCell>
        {invoice.hostedInvoiceUrl ? (
          <a
            href={invoice.hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm underline-offset-2 hover:underline"
          >
            {number}
          </a>
        ) : (
          <span className="font-mono text-sm">{number}</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[invoice.status] ?? 'muted'} className="capitalize">
          {invoice.status}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{date}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatAmount(invoice.amountDue, invoice.currency)}
      </TableCell>
      <TableCell className="text-right">
        {invoice.invoicePdf ? (
          <a
            href={invoice.invoicePdf}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download invoice ${number} PDF`}
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
          >
            <FileText className="h-4 w-4" aria-hidden />
          </a>
        ) : (
          <span aria-hidden>—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

// Stripe quotes amounts in the smallest currency unit (cents for USD). The
// dashboard only needs a readable figure, not accounting-grade formatting —
// Intl.NumberFormat handles the locale + currency symbol in one go.
function formatAmount(amountMinor: number, currency: string): string {
  const code = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(amountMinor / 100);
  } catch {
    // Unknown currency code — fall back to plain dollars-and-cents with the
    // raw code appended so operators still see what Stripe reported.
    return `${(amountMinor / 100).toFixed(2)} ${code}`;
  }
}
